import React, { useState } from 'react';
import { Grid } from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { Trans } from 'react-i18next';
import { Typography } from 'src/components/Typography';
import { Box } from 'src/components/Box';
import { WorkerProfile } from 'src/DTO/WorkerProfile.type';
import { NewJobData } from 'src/api/hooks/useJob.types';
import { BUTTON_COLOR, BUTTON_SIZE, BUTTON_VARIANT } from 'src/components/Button/types';
import { TYPOGRAPHY_COLORS, TYPOGRAPHY_WEIGHT } from 'src/components/Typography/types';
import { Button } from 'src/components/Button';
import { CreateJobOfferModal } from './CreateJobOfferModal';

export interface InviteWorkersToJobProps {
  handleNextButtonClick: (newJobData: NewJobData) => void;
  handlePreviousButtonClick: () => void;
  selectedWorkers: WorkerProfile[];
  setSelectedWorkers: React.Dispatch<React.SetStateAction<WorkerProfile[]>>;
  newJobData: NewJobData;
}

export const InviteWorkersToJob: React.FC<InviteWorkersToJobProps> = ({
  handleNextButtonClick,
  handlePreviousButtonClick,
  selectedWorkers,
  setSelectedWorkers,
  newJobData,
}) => {
  const [isInviteModalOpen, setIsInviteModalOpen] = useState<boolean>(false);
  const [recruitmentNeeded, setRecruitmentNeeded] = useState<boolean>(
    newJobData?.recruitmentNeeded || true,
  );

  const handleRecruitmentChange = (
    _event: React.MouseEvent<HTMLElement>,
    isRecruitmentNeeded: boolean,
  ) => {
    setRecruitmentNeeded(isRecruitmentNeeded);
  };

  const onSubmit = () => {
    const submitData = {
      ...newJobData,
      recruitmentNeeded,
    };

    handleNextButtonClick(submitData);
  };

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <Box mt={2} mb={2} display="flex" justifyContent="center">
            <ToggleButtonGroup
              color="info"
              value={recruitmentNeeded}
              exclusive
              onChange={handleRecruitmentChange}>
              <ToggleButton value={true}>
                <Trans>jobPage.createJobModal.recruitmentNeededLabel</Trans>
              </ToggleButton>
              <ToggleButton value={false}>
                <Trans>jobPage.createJobModal.hiddenPositionLabel</Trans>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box mb={4} display="flex" justifyContent="center">
            <Typography
              variant="body2"
              fontWeight={TYPOGRAPHY_WEIGHT.REGULAR}
              color={TYPOGRAPHY_COLORS.TEXT}>
              <Trans>
                {recruitmentNeeded
                  ? 'jobPage.createJobModal.recruitmentNeeded'
                  : 'jobPage.createJobModal.hiddenPosition'}
              </Trans>
            </Typography>
          </Box>
          <Box mb={2} mt={2}>
            <Typography variant="h5" fontWeight={TYPOGRAPHY_WEIGHT.REGULAR}>
              <Trans>jobPage.createJobForm.inviteTitle</Trans>
            </Typography>
            <Typography
              variant="body2"
              fontWeight={TYPOGRAPHY_WEIGHT.REGULAR}
              color={TYPOGRAPHY_COLORS.TEXT}>
              <Trans>jobPage.createJobModal.helperText3</Trans>
            </Typography>
          </Box>
        </Grid>
        <Typography variant="h6">
          <Trans>jobPage.createJobForm.selectedWorkers</Trans> {selectedWorkers.length}
        </Typography>
        <Box display="flex" justifyContent="center" alignItems="center" width="100%" mt={4}>
          <Box display="flex" flexDirection="column">
            <Button
              onClick={() => setIsInviteModalOpen(true)}
              variant={BUTTON_VARIANT.CONTAINED}
              size={BUTTON_SIZE.MEDIUM}
              color={BUTTON_COLOR.INFO}>
              <Trans>jobPage.createJobModal.inviteWorkersButton</Trans>
            </Button>
          </Box>
        </Box>

        <CreateJobOfferModal
          isOpen={isInviteModalOpen}
          setIsOpen={setIsInviteModalOpen}
          workersToOfferJob={selectedWorkers}
          setWorkersToOfferJob={setSelectedWorkers}
        />

        <Grid item xs={12}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mt={4}>
            <Box mr={3}>
              <Button onClick={handlePreviousButtonClick}>
                <Trans>generic.previous</Trans>
              </Button>
            </Box>
            <Box ml={3}>
              <Button
                variant={BUTTON_VARIANT.GRADIENT}
                color={BUTTON_COLOR.DARK}
                type="submit"
                onClick={onSubmit}>
                <Trans>jobPage.createRoleForm.submitButtonLabel</Trans>
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};
